import { useState, useRef, useEffect, useContext } from "react";
import ScratchEditor from "./ScratchEditor";
import ScratchEditorHeader from "./ScratchEditorHeader";
import { CodeFile, ScratchInstructions } from "utils/interfaces";
import {
  EditorContext,
  ScratchEnvironmentContext,
} from "components/utils/Contexts";
import { getCollectionRequest } from "services/apiRequests";
import { useParams } from "react-router-dom";
import {
  getScratchFileFromAWS,
  sendFilesToS3ByPresignedPost,
  upsertCodeRequest,
} from "services/filesRequests";
import UnsavedCodeAlertPopUp from "../utils/pop-up-panels/unsaved-code-alert";
import { AnimatePresence } from "framer-motion";
import { usePlatform } from "components/utils/PlatformContext";
import { getUserIdFromToken } from "services/userRequests";
import { postScratchRequest } from "./utils/postScratchRequest";
import { fixZip } from "utils/turbowarp-sb3fix";

interface ScratchEnvironmentProps {
  name: string;
  video: string;
  pdf: string;
}

const ScratchEnvironment: React.FC<ScratchEnvironmentProps> = ({
  name,
  video,
  pdf,
}) => {
  //------------- Demo for how to send data to iframe-------------
  // Get iframe reference
  /* const scratch_iframe = document.getElementById('scratch-gui') as HTMLIFrameElement;
    //Retrieve correct iframe with .contentWindow, then use .postMessage as communication protocol
    const sendMessageToScratchGUI = (message: string) => {
      if (scratch_iframe && scratch_iframe.contentWindow){
        scratch_iframe.contentWindow.postMessage(message, 'http://localhost:8602'); // specify desination address
      }
    };
  
  //------------ Demo for how to receive data from iframe --------------------
  // Add an event listener
  window.addEventListener('message', function(event: any) {
        if (event.origin !== 'http://localhost:8602') return; // only accept messages from specified address
        console.log("Message from GUI: ", event.data);
  });
  */

  // States definition
  const [sb3File, setSb3File] = useState<ArrayBuffer | null>(null);
  const [sb3Error, setSb3Error] = useState<string | null>(null);
  const [sb3Loading, setSb3Loading] = useState<boolean>(false);
  const [currentCode, setCurrentCode] = useState<CodeFile[]>([]);
  const [guiReady, setGuiReady] = useState(false);
  const [stepByStepInstructions, setStepByStepInstructions] = useState<
    ScratchInstructions[]
  >([]);
  // Controller used to coordinate the pointer event behaviour of the Scratch
  // editor for smooth PDF dragging and resizing process of the PDF panel
  const [isDraggingResizingPanel, setIsDraggingResizingPanel] = useState(false);
  // Auxiliar state used to get the highlighted scratch element on the onboarding
  // walkthrough
  const [highlightedScratchElement, setHighlightedScratchElement] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });
  // Auxiliar state for scaling the scratch iframe under the min-height
  const [scaleResizeIframe, setScaleResizeIframe] = useState(1);
  const [videoExpanded, setVideoExpanded] = useState(false);
  const [activeItem, setActiveItem] = useState<"editor" | "assistant">(
    "editor"
  );
  const [showUnsavedChangesPopUp, setShowUnsavedChangesPopUp] = useState(false);
  const [isUnsavedProject, setIsUnsavedProject] = useState(false);
  // Auxiliar state for saving the current step in step-by-step instructions
  const [currentInstructionsStep, setCurrentInstructionsStep] = useState(0);

  // Get the url parameters
  const { moduleOrProject, id } = useParams();

  // Get the platform
  const { platform } = usePlatform();

  // Get the context parameters
  const { goToDashboard } = useContext(EditorContext);

  // Reference for the header and body
  const scratchIframeRef = useRef<HTMLIFrameElement | null>(null);
  const unsavedPopUpRef = useRef<any>(null);

  // For uploading file to  scratch editor
  const handleFileSelect = async (file: File) => {
    const arrayBuffer = await file.arrayBuffer();
    setSb3File(arrayBuffer);
  };

  /**
   * Once the Scratch environment begins, ask for the instructions
   */
  useEffect(() => {
    getCollectionRequest(
      `/api/lessons/instructions?id=${id}&type=${moduleOrProject}`
    ).then((response) => {
      // Check if the response went right
      if (!response.successful) return;

      // Get the data
      const data = response.content;

      // Check if the data is not empty
      if (!(data && data.length > 0)) return;

      // And set the instructions
      setStepByStepInstructions(data[0].stepByStepInstructions);
    });

    // And fetch the SB3 files
    handleFetchSb3File();
  }, []);

  /**
   * Function used to get the SB3 file from AWS
   */
  const handleFetchSb3File = async () => {
    setSb3Loading(true);
    setSb3Error(null);

    try {
      const file = await getScratchFileFromAWS(id || "", moduleOrProject || "");
      if (file instanceof ArrayBuffer) setSb3File(file);
      else if (file === undefined) setSb3File(null);
      else throw new Error("Invalid file retrieved");
    } catch (error) {
      console.error("Scratch error:", error);
      setSb3Error(
        error instanceof Error ? error.message : "An unknown error occurred"
      );
    } finally {
      setSb3Loading(false);
    }
  };

  /**
   * Function used to save a scratch file on a S3 bucket using a presigned post
   * strategy
   * @param file
   * @returns
   */
  const handleSendSavedFileToS3 = async (file: string | ArrayBuffer) => {
    // Define the code data
    const codeData = {
      type: moduleOrProject,
      itemId: id,
    };

    // Update the code
    const response = await upsertCodeRequest(codeData, "scratch");

    // Log the error
    if (!response.successful) console.log("Error when saving code.");

    // Get the userId from the cookies
    const userId = getUserIdFromToken();

    // Send the file
    const s3CodeUpload = await sendFilesToS3ByPresignedPost(
      file as string,
      `${platform}/code/scratch/${moduleOrProject}/${id}/${userId}/scratchCode.sb3`,
      response.content
    );

    // Check if the unsaved pop up is defined
    if (unsavedPopUpRef && unsavedPopUpRef.current) {
      // Check if the code was successfully uploaded
      if (s3CodeUpload) unsavedPopUpRef.current.setSavedStatus();
    }

    return s3CodeUpload;
  };

  /**
   * Function used to load the initial code
   */
  const loadScratchCode = (sb3File: ArrayBuffer | null | undefined) => {
    if (guiReady && sb3File !== undefined && sb3File !== null) {
      // Pass the sb3 file through a fixing routine
      fixZip(sb3File).then((fixedZip) => {
        postScratchRequest(scratchIframeRef, "LOAD_PROJECT", {
          projectData: fixedZip,
        });
      });
    }
  };

  return (
    <ScratchEnvironmentContext.Provider
      value={{
        isDraggingResizingPanel,
        setIsDraggingResizingPanel,
        highlightedScratchElement,
        setHighlightedScratchElement,
        guiReady,
        setGuiReady,
        scaleResizeIframe,
        setScaleResizeIframe,
        stepByStepInstructions,
        pdf,
        sb3File,
        setShowUnsavedChangesPopUp,
        handleSendSavedFileToS3,
        isUnsavedProject,
        setIsUnsavedProject,
        currentCode,
        loadScratchCode,
        currentInstructionsStep,
        setCurrentInstructionsStep,
      }}
    >
      <div className="flex-grow relative">
        <div className="h-screen overflow-hidden">
          <ScratchEditorHeader
            name={name}
            video={video}
            activeItem={activeItem}
            setActiveItem={setActiveItem}
            videoExpanded={videoExpanded}
            setVideoExpanded={setVideoExpanded}
            showAssistant={true}
            onFileSelect={handleFileSelect}
            scratchIframe={scratchIframeRef}
          />
          {!sb3Loading && !sb3Error && (
            <ScratchEditor
              sb3File={sb3File}
              videoExpanded={videoExpanded}
              iframeRef={scratchIframeRef}
              onFileChange={(x) => setCurrentCode(x)}
            />
          )}
          {/* Unsaved changes pop up message */}
          <AnimatePresence>
            {showUnsavedChangesPopUp && (
              <UnsavedCodeAlertPopUp
                ref={unsavedPopUpRef}
                onBgClick={() => setShowUnsavedChangesPopUp(false)}
                onSave={() =>
                  postScratchRequest(scratchIframeRef, "SAVE_PROJECT")
                }
                onClosePopUp={() => setShowUnsavedChangesPopUp(false)}
                onCloseWithoutSaving={() => goToDashboard()}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </ScratchEnvironmentContext.Provider>
  );
};

export default ScratchEnvironment;
